export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
}

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface HapticFeedback {
  impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
  notificationOccurred(type: "error" | "success" | "warning"): void;
  selectionChanged(): void;
}

interface WebApp {
  initData: string;
  initDataUnsafe: {
    user?: TelegramUser;
    query_id?: string;
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: "light" | "dark";
  themeParams: ThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  HapticFeedback: HapticFeedback;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
  onEvent(eventType: string, callback: () => void): void;
  offEvent(eventType: string, callback: () => void): void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: WebApp;
    };
  }
}

function getWebApp(): WebApp | null {
  if (typeof window === "undefined") return null;
  return window.Telegram?.WebApp ?? null;
}

export function getTelegramUser(): TelegramUser | null {
  return getWebApp()?.initDataUnsafe?.user ?? null;
}

export function getInitData(): string {
  return getWebApp()?.initData ?? "";
}

export function getColorScheme(): "light" | "dark" {
  return getWebApp()?.colorScheme ?? "dark";
}

export function setHeaderColor(color: string): void {
  getWebApp()?.setHeaderColor(color);
}

export function setBackgroundColor(color: string): void {
  getWebApp()?.setBackgroundColor(color);
}

export function enableClosingConfirmation(): void {
  getWebApp()?.enableClosingConfirmation();
}

export function expandApp(): void {
  getWebApp()?.expand();
}

export function markAppReady(): void {
  getWebApp()?.ready();
}

export function hapticFeedback(
  type: "impact" | "notification" | "selection",
  style?: "light" | "medium" | "heavy" | "rigid" | "soft",
  notificationType?: "error" | "success" | "warning",
): void {
  const webApp = getWebApp();
  if (!webApp) return;

  switch (type) {
    case "impact":
      webApp.HapticFeedback.impactOccurred(style ?? "light");
      break;
    case "notification":
      webApp.HapticFeedback.notificationOccurred(notificationType ?? "success");
      break;
    case "selection":
      webApp.HapticFeedback.selectionChanged();
      break;
  }
}

export function isTelegramEnvironment(): boolean {
  return getWebApp() !== null;
}
